import React, { useEffect, useRef } from 'react';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ArrowLeft, Delete } from 'Components/DesignSystem/Iconography/Icons';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';

// Module Components
import FieldConfigurationPanel from './FieldConfigurationPanel';

// Constants
import { STRINGS } from '../../../constants';

// Types
import {
  CtaConfiguration,
  DescriptionStyle,
  FieldAlignment,
  FieldDefinition,
  FieldConfiguration,
  ImageFit,
} from '../../../types';

interface PropertiesPanelProps {
  definition: FieldDefinition;
  configuration: FieldConfiguration;
  /** The region the element sits in, worded as R48 words it. */
  regionName: string;
  /** Card-wide UOM, read and written by the Pricing section (spec R69). */
  /** Absent for an element with no block of its own to remove (spec R63, R72). */
  onRemove: (() => void) | null;
  onBack: () => void;
  onDisplayLabelChange: (displayLabel: string) => void;
  onAlignmentChange: (alignment: FieldAlignment) => void;
  onImageFitChange: (imageFit: ImageFit) => void;
  onDescriptionStyleChange: (descStyle: DescriptionStyle) => void;
  onShowUomChange: (showUom: boolean) => void;
  onStrikethroughChange: (strikethrough: boolean) => void;
  onCtaOptionsChange: (options: Partial<CtaConfiguration>) => void;
  onCustomCssChange: (customCss: string) => void;
}

/**
 * One element's properties, as the editor's **third column** (spec R63, R65).
 *
 * It is mounted by the editor's shell as the last child of the body's row, not by
 * the configuration pane: 320px wide, never shrinking, stretched to the body's full
 * height, and separated from the preview by a left border. Being an in-flow column
 * is what reserves its width, so nothing it renders can ever cover the preview
 * (ADR-3A) and the pane's own two sections are untouched by it opening (R59).
 *
 * It does not know it can be collapsed (spec R76): the shell unmounts this whole
 * subtree while the column is closed, so a collapsed panel leaves the accessibility
 * tree instead of lingering as a zero-width box with focusable children in it
 * (ADR-5D). Every value it shows comes from the card and its target from
 * `useInspector`, so remounting restores the panel exactly as it was.
 *
 * Inside, one scroll container holds the whole panel: stretched to the column's own
 * bounded height, it scrolls its sections and nothing else moves (spec R74). It was
 * `sticky` + `max-h-screen` before, which pinned the panel to a *page* scroll — the
 * posture the prototype had, and the wrong one here, because 100vh is taller than the
 * space this app's 60px header leaves, so the ceiling never arrived and the wheel
 * scrolled all three columns instead.
 *
 * The same panel opens from a row's chevron (R42) and from selecting a block in the
 * preview (R55), because both write the one inspector target.
 *
 * Focus moves to the heading when the panel opens and the back control hands it to
 * the field's row on the way out (NFR A1) — a labelled region, deliberately not a
 * focus trap: the pane and the preview stay reachable by keyboard while it is open.
 */
const PropertiesPanel = ({
  definition,
  configuration,
  regionName,
  onRemove,
  onBack,
  onDisplayLabelChange,
  onAlignmentChange,
  onImageFitChange,
  onDescriptionStyleChange,
  onShowUomChange,
  onStrikethroughChange,
  onCtaOptionsChange,
  onCustomCssChange,
}: PropertiesPanelProps) => {
  const headingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [definition.id]);

  return (
    <section
      aria-label={`${definition.name} — ${regionName}`}
      className="tw-w-80 tw-h-full tw-shrink-0 tw-self-stretch tw-bg-surface tw-border-l tw-border-edge"
    >
      {/* The column's own scroll container: the full height of the column, and the
          only thing that moves when the properties overflow it (spec R63, R74). The
          heading it holds keeps its `tabIndex` and its focus-on-open either way. */}
      <div className="md:tw-h-full md:tw-overflow-y-auto">
        {/* The panel header: back, then WHERE › WHAT — the region as an eyebrow above the
            element's name — then remove. It was the region as an uppercase pill beside the
            name: in the 320px column the pair did not fit one line, so the pill wrapped under
            the title, the header became two rows the controls centred on, and the pill (a
            tall grey chip) outweighed the name it described. As an eyebrow the region reads
            as context, sits in the same label style as the section headings below, and a
            long name wraps under it without ever adding a row. Back and remove are the same
            quiet 28px ghost, so they read as a pair; remove turns danger only on hover and
            focus — red at rest, it was the loudest thing in the header. */}
        <header className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-bg-surface tw-border-b tw-border-edge">
          <IconButton
            variant="subtle"
            size="sm"
            onClick={onBack}
            title={STRINGS.pane.closeProperties}
            aria-label={STRINGS.pane.closeProperties}
            data-testid="product-card-properties-back"
          >
            <ArrowLeft size={16} aria-hidden="true" />
          </IconButton>

          <div
            ref={headingRef}
            tabIndex={-1}
            className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accent"
          >
            <Typography
              variant="sub-title-label"
              as="span"
              className="tw-text-ink-tertiary tw-uppercase tw-tracking-wide tw-truncate"
              data-testid="product-card-properties-region"
            >
              {regionName}
            </Typography>
            <Typography
              variant="title-header"
              as="h3"
              className="tw-text-ink tw-break-words"
              data-testid="product-card-properties-title"
            >
              {definition.name}
            </Typography>
          </div>

          {onRemove && (
            <IconButton
              variant="danger-subtle"
              size="sm"
              onClick={onRemove}
              title={STRINGS.pane.removeFromCard}
              aria-label={`${STRINGS.pane.removeFromCard}: ${definition.name}`}
              /* Grey at rest, the danger pair on hover and keyboard focus. The caller's
                 `tw-text-ink-secondary` replaces the variant's resting danger ink
                 (withClassName); the danger tint on hover is the variant's own. */
              className="tw-text-ink-secondary hover:tw-text-tone-danger-ink focus-visible:tw-text-tone-danger-ink"
              data-testid="product-card-properties-remove"
            >
              <Delete size={16} aria-hidden="true" />
            </IconButton>
          )}
        </header>

        <FieldConfigurationPanel
          definition={definition}
          configuration={configuration}
          onDisplayLabelChange={onDisplayLabelChange}
          onAlignmentChange={onAlignmentChange}
          onImageFitChange={onImageFitChange}
          onDescriptionStyleChange={onDescriptionStyleChange}
          onShowUomChange={onShowUomChange}
          onStrikethroughChange={onStrikethroughChange}
          onCtaOptionsChange={onCtaOptionsChange}
          onCustomCssChange={onCustomCssChange}
        />
      </div>
    </section>
  );
};

export default PropertiesPanel;
